/**
 * `/` — the project's home.
 *
 * The site is the Bioregional Twin; each twin lives under its own path (spec
 * §4.1). This page says what a twin is in a minute, shows the one that
 * exists, and opens a door for the next. No WebGL here: the Front Range card
 * is a picture in CSS, and the map waits for the click.
 */
import { useEffect, useState } from "react";

import { fetchJson } from "../data/client";
import type { Conditions } from "../data/types";
import { PATHS } from "../routes";
import { HomeAtlas } from "./HomeAtlas";
import { ContactForm } from "./ContactForm";

/** How many places the Front Range twin measures right now — one fetch, no polling. */
export function usePlaceCount(): number | null {
  const [count, setCount] = useState<number | null>(null);
  useEffect(() => {
    let live = true;
    fetchJson<Conditions>("latest/conditions.json", { signal: AbortSignal.timeout(15_000) })
      .then((c) => live && setCount(c.stations?.length ?? null))
      .catch(() => live && setCount(null));
    return () => {
      live = false;
    };
  }, []);
  return count;
}

/** The route: `Home` with the one number it shows. */
export function HomePage() {
  return <Home places={usePlaceCount()} />;
}

export const COMMONS_URL = "https://prism.omniharmonic.com/p/front-range";

export interface HomeProps {
  /** The one live figure the Front Range card carries: how many places it measures. */
  places?: number | null;
}

export function Home({ places = null }: HomeProps) {
  return (
    <main className="home">
      <div className="home__opening">
      <header className="home__hero">
        <h1 className="home__title"><span>Bioregional Twin</span>Know the place<br/>you belong to.</h1>
        <p className="home__lede">
          A river has a story before it reaches you. So does the snow above it,
          the life around it, and the ground beneath your feet.
        </p>
        <p className="home__intro">Explore the connections in a living map of public measurements,
          species records and shared knowledge. Start with Colorado’s Front Range.</p>
        <p className="sheet__actions">
          <a className="btn btn--primary" href={PATHS.twin}>Open the Front Range twin</a>
          <a className="home__quiet-link" href="#agents">Connect an agent</a>
        </p>
        <p className="home__invitation">Six watersheds. Many ways to understand a place.</p>
      </header>
      <HomeAtlas />
      </div>

      <section className="home__how" aria-labelledby="home-how">
        <h2 className="home__h2" id="home-how">
          How it works
        </h2>
        <div className="home__cols">
          <div>
            <h3 className="home__h3">Evidence with context</h3>
            <p>
              Measurements carry their units, times and sources. When a
              gauge goes quiet the number goes stale and says so. Forecasts and mapped ranges are identified as such.
            </p>
          </div>
          <div>
            <h3 className="home__h3">Republished every few minutes</h3>
            <p>
              A small machine polls the public feeds and writes a static tree of files anyone can read. The
              map is a reader of those files, the same way you can be.
            </p>
          </div>
          <div>
            <h3 className="home__h3">Joined to a knowledge commons</h3>
            <p>
              Each watershed, gauge and named place has a page in a shared wiki where people write what the
              numbers cannot: history, stewardship, species, what is being tried. The map links to it, and the
              wiki links back.
            </p>
          </div>
        </div>
      </section>

      <section className="home__twins" aria-labelledby="home-twins">
        <h2 className="home__h2" id="home-twins">
          The twins
        </h2>
        <a className="twin-card" href={PATHS.landing}>
          <span className="twin-card__plate" role="img" aria-label="The Front Range twin: gauges and stations over the relief west of Boulder" />
          <span className="twin-card__body">
            <span className="twin-card__name">Front Range</span>
            <span className="twin-card__where">Colorado, between the Continental Divide and the plains</span>
            <span className="twin-card__line">
              Six watersheds, from the Cache la Poudre to Clear Creek. Snow, streamflow, air, weather, fire,
              drought and earthquakes{places ? ` across ${places.toLocaleString()} measured places` : ""}.
            </span>
            <span className="twin-card__cta">Open the Front Range</span>
          </span>
        </a>
      </section>

      <section className="home__agents" id="agents" aria-labelledby="home-agents">
        <div><h2 id="home-agents">A place can inform<br/>more than a map.</h2>
        <p>Give an agent the ecological context to speak for a creek, understand a watershed,
        or learn about a species. Ask within a boundary. Follow every answer back to its source.</p>
        <a className="btn" href="/front-range/about#agents">Connect to the twin</a></div>
        <div className="home__agent-flow"><span>Choose a place</span><span>Read its water, life & conditions</span><span>Keep the source, date & uncertainty</span>
        <p>Read-only MCP & public data API<br/>For Kami and any agent that can use tools.</p></div>
      </section>

      <section className="home__build" id="build" aria-labelledby="home-build">
        <h2 className="home__h2" id="home-build">
          Build one for your bioregion
        </h2>
        <p className="home__build-lede">
          A twin needs public feeds worth reading, a boundary that means something to the people inside it,
          and someone who will steward the result. If that is your place, tell me about it. I read every
          message and write back.
        </p>
        <ContactForm />
      </section>

      <footer className="home__foot">
        <a className="link" href={PATHS.about}>
          Where the Front Range data comes from
        </a>
        <a className="link" href={COMMONS_URL} rel="noopener">
          Front Range Knowledge Commons
        </a>
      </footer>
    </main>
  );
}
